import { Alert, Col, Flex, Form, Input, Modal, Row, Space, Table } from 'antd';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Breadcrumb, Button, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ControlPanelManager } from '../../../api/ControlPanelManager';
import { useToasts } from '../../../contexts/toasts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import IconButton from '../../../components/common/IconButton';
import AppSearch from '../../../components/common/AppSearch';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { Organization } from '../../../models/ControlPanel';

export default function OrganizationsList() {
  const [searchText, setSearchText] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>();
  const [organization, setOrganization] = useState<Organization>();
  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  useEffect(() => {
    getOrganizations();
    // eslint-disable-next-line
  }, []);

  const filteredOrganizations = useMemo(() => organizations?.filter(org => org.name.toLowerCase().includes(searchText.toLowerCase())), [searchText, organizations]);

  const reset = () => {
    setOrganizations(undefined);
    getOrganizations();
  }

  // const openDeleteModal = (group: Organization) => () => {
  //   setOrganization(group);
  //   setShowDeleteModal(true);
  // }

  const handleDeleteModalClose = () => {
    setOrganization(undefined);
    setShowDeleteModal(false);
  }
  const handleCreateModalClose = () => setShowCreateModal(false);
  const openCreateModal = () => setShowCreateModal(true);

  const deleteOrganization = () => {
    showLoader();

    ControlPanelManager.deleteGroup(organization!.id)
      .then(
        () => {
          hideLoader();
          showToast('organization-success', 'Organization deleted successfully', 'Organization Success', 'success');
          reset();
          handleDeleteModalClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('organizations-error', message, `Organizations Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const getOrganizations = () => {
    ControlPanelManager.getOrganizations()
      .then(
        ({ data }) => setOrganizations(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('organizations-error', message, `Organizations Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const createGroup = (group: Organization) => {
    showLoader();

    ControlPanelManager.createOrganization(group)
      .then(
        () => {
          hideLoader();
          handleCreateModalClose();
          showToast('group-success', 'Group created successfully', 'Group Success', 'success');
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occurred while retrieving organizations. Try again later."
      />
    )

    return (
      <Table size="middle" scroll={{ y: 'calc(100vh - 300px)' }} dataSource={filteredOrganizations} rowKey="id">
        <Table.Column
          key="name"
          dataIndex="name"
          title="Name"
          width="75%"
        />
        <Table.Column
          key="actions"
          title="Actions"
          className="text-center"
          render={(_value, record: any) => (
            <Space size={1}>
              <Link to={`${record.id}/manage-members`}>
                <IconButton tooltip="Members">
                  <FontAwesomeIcon fixedWidth icon={solid('users')} />
                </IconButton>
              </Link>
              {/* TEMPORARY DISABLING DELETE ORGS */}
              {/* <IconButton tooltip="Delete" className="text-danger ms-2" onClick={openDeleteModal(record)}>
              <FontAwesomeIcon fixedWidth icon={solid('trash')} />
            </IconButton> */}
            </Space>
          )}
        />
      </Table>
    )
  }, [filteredOrganizations, error]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>Organization Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3" align="middle">
        <Col md={6}>
          <h5 className="mb-0">Organizations List</h5>
        </Col>

        <Col flex="1 0" className="text-center px-3">
          <AppSearch placeholder="Search organization..." value={searchText} onChange={setSearchText} />
        </Col>

        <Col md={6} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Register Organization</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal
        title="Delete Organization"
        open={showDeleteModal}
        onCancel={handleDeleteModalClose}
        afterClose={handleDeleteModalClose}
        footer={
          <Flex justify="space-between" className="mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={deleteOrganization}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </Flex>
        }
      >
        <p>Are you sure you want to delete organization <b>{organization?.name}</b>?</p>

        <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
      </Modal>

      <Modal
        title="Create Organization"
        open={showCreateModal}
        onCancel={handleCreateModalClose}
        afterClose={handleCreateModalClose}
        footer={null}
      >
        <Form layout="vertical" onFinish={createGroup}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Organization Name is required' }]}>
            <Input placeholder="Organization Name" />
          </Form.Item>

          <br />

          <Flex justify="space-between">
            <Button size="sm" type="button" variant="light" className="border" onClick={handleCreateModalClose}>Cancel</Button>
            <Button size="sm" type="submit" variant="success">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Confirm</span>
            </Button>
          </Flex>
        </Form>
      </Modal>
    </Container>
  )
}
